
import React, { createContext, useContext, ReactNode, useEffect, useCallback, useMemo } from "react";
import { OnboardingContextType } from "@/types/onboarding-context";
import { useOnboardingData } from "@/hooks/useOnboardingData";
import { useOnboardingOperations } from "@/hooks/useOnboardingOperations";
import { useSaveOnboardingStep } from "@/hooks/useSaveOnboardingStep";
import { debugLogger } from '@/utils/debugLogger';

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get data and data-related functions
  const { data, updateData, loading, setLoading, lastSavedStep, onboardingCompleted, loadOnboardingData } = useOnboardingData();
  
  // Get save step functionality
  const { saveStepData, saving: savingStep } = useSaveOnboardingStep();
  
  // Get operations and navigation functions
  const { 
    currentStep, 
    totalSteps, 
    goToStep, 
    nextStep, 
    prevStep, 
    checkUsernameAvailability,
    saveOnboardingData,
    setCurrentStep,
    registerPhotoUploadCleanup
  } = useOnboardingOperations(data, setLoading, lastSavedStep);

  // Optimized updateData with value comparison to prevent unnecessary updates
  const optimizedUpdateData = useCallback((newData: any) => {
    // Check if any values actually changed
    const hasChanges = Object.keys(newData).some(key => {
      return data[key as keyof typeof data] !== newData[key];
    });
    
    if (hasChanges) {
      updateData(newData);
    }
  }, [data, updateData]);

  // Redirect if onboarding already completed
  useEffect(() => {
    if (onboardingCompleted) {
      debugLogger.info('onboarding', 'Onboarding already completed, redirecting to feed');
      window.location.href = '/main/feed';
    }
  }, [onboardingCompleted]);

  // Load onboarding data when provider mounts
  useEffect(() => {
    loadOnboardingData();
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentStep,
    totalSteps,
    goToStep,
    nextStep,
    prevStep,
    loading: loading || savingStep,
    setLoading,
    data,
    updateData: optimizedUpdateData,
    checkUsernameAvailability,
    saveOnboardingData,
    saveStepData,
    registerPhotoUploadCleanup
  }), [
    currentStep,
    totalSteps,
    goToStep,
    nextStep,
    prevStep,
    loading,
    savingStep,
    setLoading,
    data,
    optimizedUpdateData,
    checkUsernameAvailability,
    saveOnboardingData,
    saveStepData,
    registerPhotoUploadCleanup
  ]);

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      "useOnboarding must be used within an OnboardingProvider"
    );
  }
  return context;
};
